import { deleteInvoiceItem, IInvoiceItem } from "@/api/invoice";
import { checkDiscountCodeInvoice, updateInvoice } from "@/api/orders.ts";
import { IStoreOrder, updateStoreOrder } from "@/api/store";
import { getPaymentBreakdown } from "@/utils";
import { calculateTotalExcludingDiscount } from "@/utils/getPaymentBreakdown";
import {
    Box,
    Button,
    Card,
    Divider,
    InputAdornment,
    Link,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useAsync } from "@react-hookz/web";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export interface ICheckoutFormProps {
    order: IStoreOrder;
    invoiceItem: IInvoiceItem;
}

export const InvoiceCheckoutForm = ({
    invoiceItem,
    order,
}: ICheckoutFormProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const [checkDiscountState, checkDiscountActions] = useAsync(
        checkDiscountCodeInvoice
    );

    const amount = (invoiceItem.amount / 100).toFixed(2);
    const noDiscountTotal = (
        calculateTotalExcludingDiscount(invoiceItem) / 100
    ).toFixed(2);
    const hasDiscount = amount !== noDiscountTotal;

    const [discountCode, setDiscountCode] = useState("");

    const checkDiscount = async () => {
        if (discountCode === "") {
            enqueueSnackbar({
                variant: "error",
                message: "You must provide a discount code",
            });
            return;
        }
        checkDiscountActions.execute(invoiceItem.id, discountCode);
    };

    const handleFinish = async () => {
        // Finish invoice creation
        if (!order.id) {
            return;
        }
        const updatedOrder = await updateStoreOrder(order.store_id, order.id, {
            invoice_finished: true,
        });
        // set the flag in the metadata to be non draft as well
        try {
            await updateInvoice(updatedOrder.id ?? order.id);
        } catch (error) {
            enqueueSnackbar(
                "Failed to update invoice metadata. Please try again.",
                { variant: "error" }
            );
            return;
        }
        navigate(`/dashboard/stores/${order.store_id}/invoices`);
        return;
    };

    const handleCancel = async () => {
        deleteInvoiceItem(invoiceItem.id);
        navigate("/dashboard");
        return;
    };

    // TODO: handle discount code
    useEffect(() => {
        if (
            checkDiscountState.status === "success" &&
            checkDiscountState.status
        ) {
            queryClient.invalidateQueries({
                queryKey: ["checkout-invoice-item"],
            });
        }

        if (checkDiscountState.status === "error") {
            const error = checkDiscountState.error as AxiosError<{
                detail: string;
            }>;
            if (
                error.response &&
                error.response.data &&
                error.response.data.detail
            ) {
                enqueueSnackbar({
                    variant: "error",
                    message: error.response.data.detail,
                });
            }
        }
    }, [checkDiscountState, queryClient]);

    return (
        <>
            <Card>
                <Box
                    display={"flex"}
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent={"space-between"}
                >
                    <Box flex={1} m={{ md: 3, xs: 2 }}>
                        <Card
                            sx={{
                                backgroundColor: "grey.200",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {/* Payment Details Container */}
                            <Typography
                                variant="h6"
                                component="h2"
                                fontWeight={600}
                                gutterBottom
                                pt={2}
                                px={2}
                            >
                                Payment Details
                            </Typography>
                            <Divider />

                            <Box my={1} px={2}>
                                <Stack gap={0.5}>
                                    {getPaymentBreakdown(invoiceItem, order)
                                        .filter((item) => item.value !== null)
                                        .map(
                                            ({ label, desc, value }, index) => (
                                                <Stack
                                                    key={index}
                                                    direction="row"
                                                    mb={1}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        textAlign={"left"}
                                                        textTransform={
                                                            "capitalize"
                                                        }
                                                        flex={1}
                                                    >
                                                        {label}
                                                    </Typography>
                                                    {desc && (
                                                        <Typography
                                                            variant="caption"
                                                            textAlign={"left"}
                                                            textTransform={
                                                                "capitalize"
                                                            }
                                                            mx={1}
                                                            flex={1}
                                                        >
                                                            {desc}
                                                        </Typography>
                                                    )}

                                                    <Typography
                                                        variant="body2"
                                                        textAlign={"right"}
                                                        flex={1}
                                                    >
                                                        $
                                                        {(
                                                            Number(value) / 100
                                                        ).toFixed(2)}
                                                    </Typography>
                                                </Stack>
                                            )
                                        )}
                                </Stack>
                            </Box>
                            <Box flexGrow={1} />

                            <Box>
                                <Divider />
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    mt={2}
                                    px={2}
                                    pb={2}
                                >
                                    <Typography fontWeight={900} fontSize={18}>
                                        Total
                                    </Typography>
                                    <Box>
                                        {hasDiscount && (
                                            <Typography
                                                color={"grey"}
                                                sx={{
                                                    textDecoration:
                                                        "line-through",
                                                }}
                                            >
                                                ${noDiscountTotal}
                                            </Typography>
                                        )}
                                        <Typography>${amount}</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Card>
                    </Box>

                    {/* Stripe inputs */}
                    <Box flex={1} m={{ md: 3, xs: 2 }}>
                        <TextField
                            sx={{ mb: 1 }}
                            fullWidth
                            size="small"
                            label="Discount Code"
                            variant="outlined"
                            disabled={hasDiscount}
                            value={discountCode}
                            onChange={(e) => {
                                setDiscountCode(e.target.value);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button
                                            variant="text"
                                            disabled={hasDiscount}
                                            onClick={() => checkDiscount()}
                                        >
                                            Apply
                                        </Button>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <form id="payment-form">
                            <Typography
                                variant="body2"
                                component="h2"
                                color={"black"}
                                mb={1}
                            >
                                Email
                            </Typography>

                            <Box>
                                <input
                                    id="email"
                                    type="text"
                                    value={order.email}
                                    placeholder="Enter email address"
                                    disabled
                                    style={{
                                        padding: "10px",
                                        fontSize: "16px",
                                        borderRadius: "4px",
                                        border: "1px solid #ccc",
                                        width: "100%",
                                        backgroundColor: "#f0f0f0",
                                        color: "#a9a9a9",
                                        marginBottom: "10px",
                                    }}
                                />

                                {/* <PaymentElement
                                    id="payment-element"
                                    options={{ layout: "tabs" }}
                                /> */}
                            </Box>

                            <Button
                                variant={"contained"}
                                onClick={handleFinish}
                                id="submit"
                                fullWidth
                                sx={{ mt: 2 }}
                            >
                                Finish
                            </Button>
                            <Button
                                variant={"contained"}
                                onClick={handleCancel}
                                id="submit"
                                fullWidth
                                sx={{
                                    mt: 2,
                                    backgroundColor: "#e72828",
                                    "&:hover": {
                                        backgroundColor: "#c62828",
                                    },
                                }}
                            >
                                Cancel
                            </Button>
                        </form>
                    </Box>
                </Box>
            </Card>
            <Box display={"flex"} justifyContent={"center"} marginTop={1}>
                <Typography
                    fontSize={16}
                    fontWeight={700}
                    color={"gray"}
                    marginRight={1}
                >
                    Have an issue? Contact Us!
                </Typography>
                <Link marginRight={1} href="mailto:support@trukem.com">
                    support@trukem.com
                </Link>
                <Typography>+1 (330) 423-5438</Typography>
            </Box>
        </>
    );
};
